// src/lib/Companion.js
// Original animated mascots (pure SVG + CSS keyframes — no libraries, no
// image files). Three to pick from in Settings › Profile: a chubby knight,
// a cute dark horse, and a zappy lightning bolt. They bob, blink, and wiggle.
import React from 'react';

const Styles = () => (
  <style>{`
    @keyframes comp-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
    @keyframes comp-squash { 0%,100% { transform: scaleX(1) scaleY(1); } 50% { transform: scaleX(1.04) scaleY(0.96); } }
    @keyframes comp-blink { 0%, 92%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.08); } }
    @keyframes comp-shadow { 0%,100% { transform: scaleX(1); opacity: .35; } 50% { transform: scaleX(0.82); opacity: .22; } }
    @keyframes comp-wag { 0%,100% { transform: rotate(-6deg); } 50% { transform: rotate(10deg); } }
    @keyframes comp-spark { 0%,100% { opacity: 0; transform: scale(.4); } 50% { opacity: 1; transform: scale(1); } }
    @keyframes comp-plume { 0%,100% { transform: rotate(-4deg); } 50% { transform: rotate(6deg); } }
    .comp-bob { animation: comp-bob 2.6s ease-in-out infinite; transform-origin: 60px 110px; }
    .comp-squash { animation: comp-squash 2.6s ease-in-out infinite; transform-origin: 60px 110px; }
    .comp-blink { animation: comp-blink 4.2s linear infinite; transform-origin: center; transform-box: fill-box; }
    .comp-shadow { animation: comp-shadow 2.6s ease-in-out infinite; transform-origin: 60px 116px; }
    .comp-wag { animation: comp-wag 1.8s ease-in-out infinite; transform-origin: 88px 84px; }
    .comp-spark { animation: comp-spark 1.6s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
    .comp-spark2 { animation: comp-spark 1.6s ease-in-out .8s infinite; transform-origin: center; transform-box: fill-box; }
    .comp-plume { animation: comp-plume 2.6s ease-in-out infinite; transform-origin: 60px 22px; }
  `}</style>
);

// ---- Sir Sparks: chubby little knight with a wobbly plume ----
const Knight = ({ size }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <ellipse className="comp-shadow" cx="60" cy="112" rx="30" ry="5" fill="#000" />
    <g className="comp-bob">
      <g className="comp-squash">
        {/* plume */}
        <path className="comp-plume" d="M60 22 C 54 8, 68 6, 66 18 C 72 12, 76 20, 66 24 Z" fill="#f43f5e" />
        {/* helmet + body */}
        <ellipse cx="60" cy="72" rx="30" ry="34" fill="#94a3b8" />
        <ellipse cx="60" cy="72" rx="30" ry="34" fill="none" stroke="#475569" strokeWidth="2.5" />
        <path d="M30 62 a30 26 0 0 1 60 0 l0 4 -60 0 Z" fill="#cbd5e1" stroke="#475569" strokeWidth="2.5" />
        {/* visor slit + face */}
        <rect x="40" y="68" width="40" height="18" rx="9" fill="#1f2937" />
        <g className="comp-blink">
          <circle cx="52" cy="77" r="3.4" fill="#fff" />
          <circle cx="68" cy="77" r="3.4" fill="#fff" />
        </g>
        {/* rosy cheeks */}
        <circle cx="43" cy="86" r="3" fill="#f9a8d4" opacity=".7" />
        <circle cx="77" cy="86" r="3" fill="#f9a8d4" opacity=".7" />
        {/* tiny sword */}
        <g className="comp-wag">
          <rect x="86" y="58" width="4" height="26" rx="2" fill="#e5e7eb" stroke="#475569" strokeWidth="1.5" />
          <rect x="81" y="80" width="14" height="4" rx="2" fill="#a16207" />
        </g>
        {/* stubby feet */}
        <ellipse cx="48" cy="106" rx="8" ry="5" fill="#475569" />
        <ellipse cx="72" cy="106" rx="8" ry="5" fill="#475569" />
      </g>
    </g>
  </svg>
);

// ---- Midnight: small dark horse, big eyes ----
const Horse = ({ size }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <ellipse className="comp-shadow" cx="60" cy="112" rx="32" ry="5" fill="#000" />
    <g className="comp-bob">
      <g className="comp-squash">
        {/* tail */}
        <path className="comp-wag" d="M88 82 C 100 76, 102 92, 92 96 C 98 88, 92 86, 88 90 Z" fill="#312e81" />
        {/* body */}
        <ellipse cx="58" cy="86" rx="28" ry="20" fill="#1e1b4b" stroke="#4338ca" strokeWidth="2" />
        {/* head */}
        <g>
          <ellipse cx="44" cy="56" rx="20" ry="17" fill="#1e1b4b" stroke="#4338ca" strokeWidth="2" />
          {/* muzzle */}
          <ellipse cx="32" cy="62" rx="10" ry="8" fill="#312e81" />
          <circle cx="29" cy="61" r="1.6" fill="#0f0d2e" />
          <circle cx="34" cy="63" r="1.6" fill="#0f0d2e" />
          {/* ears */}
          <path d="M50 40 l4 -12 l7 10 Z" fill="#1e1b4b" stroke="#4338ca" strokeWidth="2" />
          <path d="M38 40 l0 -12 l9 8 Z" fill="#1e1b4b" stroke="#4338ca" strokeWidth="2" />
          {/* mane */}
          <path d="M56 42 C 66 44, 66 60, 60 68 C 66 62, 70 52, 64 42 Z" fill="#818cf8" />
          {/* big sparkly eyes */}
          <g className="comp-blink">
            <circle cx="42" cy="54" r="5.5" fill="#fff" />
            <circle cx="42" cy="54" r="3" fill="#0f0d2e" />
            <circle cx="43.5" cy="52.5" r="1.2" fill="#fff" />
            <circle cx="55" cy="56" r="4.5" fill="#fff" />
            <circle cx="55" cy="56" r="2.5" fill="#0f0d2e" />
            <circle cx="56" cy="54.8" r="1" fill="#fff" />
          </g>
          {/* blush */}
          <circle cx="36" cy="66" r="2.6" fill="#f9a8d4" opacity=".6" />
        </g>
        {/* legs */}
        <rect x="42" y="98" width="7" height="10" rx="3.5" fill="#312e81" />
        <rect x="56" y="100" width="7" height="9" rx="3.5" fill="#312e81" />
        <rect x="70" y="98" width="7" height="10" rx="3.5" fill="#312e81" />
      </g>
    </g>
  </svg>
);

// ---- Zappy: a lightning bolt with a face ----
const Bolt = ({ size }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <ellipse className="comp-shadow" cx="60" cy="112" rx="24" ry="5" fill="#000" />
    <g className="comp-bob">
      <g className="comp-squash">
        {/* bolt body */}
        <path d="M66 16 L 40 62 L 56 62 L 48 104 L 82 52 L 64 52 Z"
          fill="#fbbf24" stroke="#d97706" strokeWidth="2.5" strokeLinejoin="round" />
        {/* face */}
        <g className="comp-blink">
          <circle cx="56" cy="44" r="3.2" fill="#78350f" />
          <circle cx="67" cy="42" r="3.2" fill="#78350f" />
        </g>
        <path d="M57 52 q 5 4 10 -1" stroke="#78350f" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <circle cx="52" cy="50" r="2.4" fill="#fb923c" opacity=".7" />
        <circle cx="71" cy="48" r="2.4" fill="#fb923c" opacity=".7" />
        {/* sparks */}
        <path className="comp-spark" d="M30 38 l3 -6 l3 6 l-3 6 Z" fill="#fde68a" />
        <path className="comp-spark2" d="M88 70 l2.5 -5 l2.5 5 l-2.5 5 Z" fill="#fde68a" />
      </g>
    </g>
  </svg>
);

export const COMPANIONS = {
  knight: { label: 'Sir Sparks', sub: 'the brave little knight', Comp: Knight },
  horse: { label: 'Midnight', sub: 'the cutest dark horse', Comp: Horse },
  bolt: { label: 'Zappy', sub: 'the lightning bolt', Comp: Bolt },
};

export default function Companion({ kind = 'knight', size = 140 }) {
  const { Comp } = COMPANIONS[kind] || COMPANIONS.knight;
  return (
    <span className="inline-block">
      <Styles />
      <Comp size={size} />
    </span>
  );
}
